import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { createUserDocument, deleteUserDocument, getUserDocuments } from 'data';

interface Document {
  id: number;
  filename: string;
  key: string;
  extension: string;
  url: string;
}

interface DocumentsState {
  avatar: Document | null;
  childCertificate: Document | null;
  idCard: Document | null;
  otherDocuments: Document[];
  loading: boolean;
  error: string | null;
}

const initialState: DocumentsState = {
  avatar: null,
  childCertificate: null,
  idCard: null,
  otherDocuments: [],
  loading: false,
  error: null,
};

export const fetchAvatar = createAsyncThunk(
  'documents/fetchAvatar',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getUserDocuments('avatar');
      return response.data[0] || null;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Failed to fetch avatar');
    }
  },
);

export const fetchChildCertificate = createAsyncThunk(
  'documents/fetchChildCertificate',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getUserDocuments('child_certificate');
      return response.data[0] || null;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || 'Failed to fetch child certificate',
      );
    }
  },
);

export const fetchIdCard = createAsyncThunk(
  'documents/fetchIdCard',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getUserDocuments('id_card');
      return response.data[0] || null;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Failed to fetch ID card');
    }
  },
);

export const fetchOtherDocuments = createAsyncThunk(
  'documents/fetchOtherDocuments',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getUserDocuments('');
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || 'Failed to fetch other documents',
      );
    }
  },
);

// Keep the original CRUD operations
export const addUserDocument = createAsyncThunk(
  'documents/addUserDocument',
  async (
    { file, docType }: { file: File; docType: string },
    { rejectWithValue },
  ) => {
    try {
      const formData = new FormData();
      formData.append('files[]', file);
      const response = await createUserDocument(formData, docType);
      return { document: response.data, docType };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || 'Failed to create document',
      );
    }
  },
);

export const removeUserDocument = createAsyncThunk(
  'documents/removeUserDocument',
  async ({ id }: { id: number }, { rejectWithValue }) => {
    try {
      await deleteUserDocument(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || 'Failed to delete document',
      );
    }
  },
);

const documentsSlice = createSlice({
  name: 'documents',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Avatar
      .addCase(fetchAvatar.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchAvatar.fulfilled,
        (state, action: PayloadAction<Document | null>) => {
          state.loading = false;
          state.avatar = action.payload;
        },
      )
      .addCase(fetchAvatar.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Child Certificate
      .addCase(fetchChildCertificate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchChildCertificate.fulfilled,
        (state, action: PayloadAction<Document | null>) => {
          state.loading = false;
          state.childCertificate = action.payload;
        },
      )
      .addCase(fetchChildCertificate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // ID Card
      .addCase(fetchIdCard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchIdCard.fulfilled,
        (state, action: PayloadAction<Document | null>) => {
          state.loading = false;
          state.idCard = action.payload;
        },
      )
      .addCase(fetchIdCard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Other Documents
      .addCase(fetchOtherDocuments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchOtherDocuments.fulfilled,
        (state, action: PayloadAction<Document[]>) => {
          state.loading = false;
          state.otherDocuments = action.payload;
        },
      )
      .addCase(fetchOtherDocuments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Add document
      .addCase(addUserDocument.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        addUserDocument.fulfilled,
        (
          state,
          action: PayloadAction<{ document: Document; docType: string }>,
        ) => {
          state.loading = false;
          switch (action.payload.docType) {
            case 'avatar':
              state.avatar = action.payload.document;
              break;
            case 'child_certificate':
              state.childCertificate = action.payload.document;
              break;
            case 'id_card':
              state.idCard = action.payload.document;
              break;
            default:
              state.otherDocuments.push(action.payload.document);
          }
        },
      )
      .addCase(addUserDocument.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Remove document
      .addCase(removeUserDocument.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        removeUserDocument.fulfilled,
        (state, action: PayloadAction<number>) => {
          state.loading = false;
          if (state.avatar?.id === action.payload) state.avatar = null;
          if (state.childCertificate?.id === action.payload)
            state.childCertificate = null;
          if (state.idCard?.id === action.payload) state.idCard = null;
          state.otherDocuments = state.otherDocuments.filter(
            (doc) => doc.id !== action.payload,
          );
        },
      )
      .addCase(removeUserDocument.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError } = documentsSlice.actions;
export const documentsReducer = documentsSlice.reducer;
